/**
 * @fileoverview Mock-only in-memory notification history.
 * @description Seeds the notification list from mock-notifications.json and holds it in a
 * module-level array so marking one as read mutates a list that persists for the rest of
 * the browser session — not to localStorage, matching features/sales's own mock stores.
 * Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import seedData from './mock-notifications.json'

import type {
  InAppNotification,
  InAppNotificationStatus,
} from '@/features/notifications/services/notifications.service'

// mock-notifications.json's `inAppStatus` widens to `string` on import (JSON has no
// const-assertion), so this restores the real status union.
const notifications: InAppNotification[] = seedData.map((item) => ({
  ...item,
  inAppStatus: item.inAppStatus as InAppNotificationStatus,
  readAt: item.readAt ?? null,
}))

/**
 * @description Lists every mock notification, most recent first.
 * @returns {InAppNotification[]} A copy of the notification list.
 */
export function listNotifications(): InAppNotification[] {
  return [...notifications].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/**
 * @description Counts the mock notifications still unread.
 * @returns {number} The unread count.
 */
export function countUnreadNotifications(): number {
  return notifications.filter((item) => item.inAppStatus === 'UNREAD').length
}

/**
 * @description Marks one mock notification as read.
 * @param {string} notificationDeliveryId - The notification's delivery id.
 * @returns {InAppNotification | null} The updated notification, or null when the id is unknown.
 */
export function markNotificationRead(notificationDeliveryId: string): InAppNotification | null {
  const notification = notifications.find(
    (item) => item.notificationDeliveryId === notificationDeliveryId,
  )
  if (!notification) {
    return null
  }
  if (notification.inAppStatus === 'UNREAD') {
    notification.inAppStatus = 'READ'
    notification.readAt = new Date().toISOString()
  }
  return { ...notification }
}
